/**
 * Avstämning mot en Swish-rapport.
 *
 * Föreningen laddar ned sina transaktioner från Swish-portalen och laddar upp
 * filen i lagvyn. Varje rad matchas mot en beställning via referensen i
 * meddelandefältet, och de som stämmer bockas av på en gång.
 *
 * Två saker styr hela designen:
 *
 * 1. **Formatet är okänt.** Kolumnnamn, avgränsare och talformat skiljer sig
 *    mellan banker och ändras över tid. Tolkningen letar därför efter
 *    kolumner på flera namn och känner av avgränsaren i stället för att anta
 *    ett format. Går filen ändå inte att läsa ska felet säga vad som saknas,
 *    inte bara "kunde inte tolka filen".
 *
 * 2. **Ingenting bockas av på en gissning.** Varje rad hamnar i en tydlig
 *    kategori, och bara de som matchar på både referens och belopp föreslås
 *    för avbockning. En betalning på fel belopp är inte ett fel som ska
 *    städas bort automatiskt – den är något någon behöver titta på.
 */

import { ORDER_PREFIX, orderPaymentMessage, parseOrderReference } from './orders'

/* ------------------------------------------------------------------ *
 * CSV
 * ------------------------------------------------------------------ */

/** Avgränsare vi känner igen, i den ordning de provas. */
const DELIMITERS = [';', ',', '\t']

/**
 * Gissar avgränsare genom att räkna vilken som ger flest kolumner konsekvent.
 *
 * Semikolon först: svenska bankexporter använder nästan alltid det, eftersom
 * komma är decimaltecken här.
 */
export function detectDelimiter(text: string): string {
  const lines = text.split(/\r?\n/).filter((line) => line.trim()).slice(0, 10)
  if (lines.length === 0) return ';'

  let best = ';'
  let bestScore = 0

  for (const delimiter of DELIMITERS) {
    const counts = lines.map((line) => splitRow(line, delimiter).length)
    const columns = counts[0]
    // En riktig avgränsare ger samma antal kolumner på varje rad, och fler
    // än en. Ett komma inuti ett meddelande ger ojämna rader.
    const consistent = columns > 1 && counts.every((count) => count === columns)
    if (consistent && columns > bestScore) {
      best = delimiter
      bestScore = columns
    }
  }
  return best
}

/** Delar en rad och respekterar citattecken. */
function splitRow(line: string, delimiter: string): string[] {
  const cells: string[] = []
  let current = ''
  let quoted = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      // Två citattecken i rad är ett bokstavligt citattecken.
      if (quoted && line[i + 1] === '"') {
        current += '"'
        i++
      } else {
        quoted = !quoted
      }
    } else if (char === delimiter && !quoted) {
      cells.push(current)
      current = ''
    } else {
      current += char
    }
  }
  cells.push(current)
  return cells.map((cell) => cell.trim())
}

/** Delar upp filen i rader och tar hänsyn till radbrytningar inuti citat. */
export function parseCsv(text: string, delimiter: string): string[][] {
  // BOM först, som Excel lägger dit. Utan det blir första kolumnnamnet
  // osynligt fel och kolumnmatchningen missar det.
  const clean = text.replace(/^﻿/, '')

  const rows: string[][] = []
  let line = ''
  let quoted = false

  for (let i = 0; i < clean.length; i++) {
    const char = clean[i]
    if (char === '"') quoted = !quoted
    if ((char === '\n' || char === '\r') && !quoted) {
      if (line.trim()) rows.push(splitRow(line, delimiter))
      line = ''
      // \r\n räknas som en radbrytning, inte två.
      if (char === '\r' && clean[i + 1] === '\n') i++
    } else {
      line += char
    }
  }
  if (line.trim()) rows.push(splitRow(line, delimiter))
  return rows
}

/* ------------------------------------------------------------------ *
 * Kolumner
 * ------------------------------------------------------------------ */

/**
 * Namn vi letar efter, per fält.
 *
 * Listorna är avsiktligt generösa. Kostnaden för ett extra alias är noll;
 * kostnaden för ett som saknas är att kassören får ett obegripligt fel och
 * ger upp.
 */
const COLUMN_ALIASES = {
  message: [
    'meddelande',
    'meddelande till mottagare',
    'message',
    'text',
    'kommentar',
    'referens',
    'reference',
    'ocr/meddelande',
    'notering',
  ],
  amount: ['belopp', 'belopp sek', 'belopp (sek)', 'amount', 'summa', 'transaktionsbelopp'],
  date: [
    'datum',
    'transaktionsdatum',
    'bokföringsdatum',
    'tidpunkt',
    'date',
    'tid',
    'betaldatum',
  ],
  sender: ['avsändare', 'från', 'namn', 'sender', 'payer', 'betalare', 'kundnamn'],
  status: ['status', 'transaktionsstatus'],
} as const

export type ColumnKey = keyof typeof COLUMN_ALIASES

function normalizeHeader(value: string): string {
  return value
    .toLowerCase()
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Hittar vilken kolumn som är vad. Saknade fält blir -1. */
export function mapColumns(header: string[]): Record<ColumnKey, number> {
  const normalized = header.map(normalizeHeader)
  const result = {} as Record<ColumnKey, number>

  for (const key of Object.keys(COLUMN_ALIASES) as ColumnKey[]) {
    const aliases: readonly string[] = COLUMN_ALIASES[key]
    // Exakt träff först, annars en kolumn som innehåller aliaset. Ordningen
    // spelar roll: "belopp" ska inte matcha "avgift belopp" när det finns en
    // kolumn som heter just "belopp".
    let index = normalized.findIndex((name) => aliases.includes(name))
    if (index === -1) {
      index = normalized.findIndex((name) =>
        aliases.some((alias) => name.includes(alias)),
      )
    }
    result[key] = index
  }
  return result
}

/* ------------------------------------------------------------------ *
 * Belopp
 * ------------------------------------------------------------------ */

/**
 * Tolkar ett belopp ur svensk eller engelsk skrivning.
 *
 * Hanterar "1 100,00", "1.100,00", "1100.00", "1 100 kr" och hårt mellanslag,
 * som bankexporter använder som tusentalsavgränsare oftare än vanligt
 * mellanslag. Returnerar null när strängen inte är ett tal.
 */
export function parseAmount(raw: string): number | null {
  if (!raw) return null

  let text = raw
    .replace(/ | /g, ' ')
    .replace(/(kr|sek)/gi, '')
    .replace(/\s/g, '')
    .trim()

  if (!text) return null

  const negative = text.startsWith('-') || /^\(.*\)$/.test(text)
  text = text.replace(/^-/, '').replace(/^\(|\)$/g, '')

  const lastComma = text.lastIndexOf(',')
  const lastDot = text.lastIndexOf('.')

  if (lastComma > -1 && lastDot > -1) {
    // Den sista av dem är decimaltecknet, den andra är tusentalsavgränsare.
    if (lastComma > lastDot) text = text.replace(/\./g, '').replace(',', '.')
    else text = text.replace(/,/g, '')
  } else if (lastComma > -1) {
    // Ensamt komma: decimaltecken om det följs av högst två siffror,
    // annars tusentalsavgränsare. "1,50" är en och femtio, "1,100" är 1100.
    const decimals = text.length - lastComma - 1
    text = decimals <= 2 ? text.replace(',', '.') : text.replace(/,/g, '')
  }

  const value = Number(text)
  if (!Number.isFinite(value)) return null
  return negative ? -value : value
}

/* ------------------------------------------------------------------ *
 * Matchning
 * ------------------------------------------------------------------ */

export type MatchStatus =
  | 'matched'
  | 'amountMismatch'
  | 'alreadyPaid'
  | 'cancelled'
  | 'unknownReference'
  | 'noReference'
  | 'notAPayment'

export const MATCH_LABELS: Record<MatchStatus, string> = {
  matched: 'Matchar',
  amountMismatch: 'Fel belopp',
  alreadyPaid: 'Redan avbockad',
  cancelled: 'Avbruten beställning',
  unknownReference: 'Okänd referens',
  noReference: 'Ingen referens',
  notAPayment: 'Inte en inbetalning',
}

export interface ReportRow {
  /** Radnummer i filen, räknat från 1 med rubrikraden inräknad. */
  line: number
  date: string
  message: string
  sender: string
  amountSek: number | null
  reference: string | null
  status: MatchStatus
  /** Beställningens belopp, när en beställning hittats. */
  expectedSek?: number
  buyerName?: string
}

/** Det tolkningen behöver veta om befintliga beställningar. */
export interface KnownOrder {
  reference: string
  totalSek: number
  status: 'awaitingPayment' | 'paid' | 'cancelled'
  buyerName: string
}

export interface ReportResult {
  rows: ReportRow[]
  /** Referenser som går att bocka av utan vidare. */
  readyToSettle: string[]
  counts: Record<MatchStatus, number>
  /** Fel som gör att filen inte går att använda alls. */
  problem: string | null
}

/**
 * Läser en rapport och klassar varje rad.
 *
 * Kastar aldrig. En trasig fil ska ge ett begripligt besked i gränssnittet,
 * inte en vit skärm mitt i avstämningen.
 */
export function readSwishReport(text: string, orders: KnownOrder[]): ReportResult {
  const empty = (problem: string): ReportResult => ({
    rows: [],
    readyToSettle: [],
    counts: emptyCounts(),
    problem,
  })

  if (!text.trim()) return empty('Filen är tom.')

  const delimiter = detectDelimiter(text)
  const table = parseCsv(text, delimiter)
  if (table.length < 2) {
    return empty('Filen innehåller ingen data under rubrikraden.')
  }

  /*
   * Rubrikraden ligger inte alltid först. Flera bankexporter inleder med
   * kontonummer och uttagsperiod på egna rader, så vi letar efter den första
   * raden som ser ut som rubriker.
   */
  let headerIndex = -1
  let columns: Record<ColumnKey, number> | null = null
  for (let i = 0; i < Math.min(table.length, 15); i++) {
    const candidate = mapColumns(table[i])
    if (candidate.message > -1 && candidate.amount > -1) {
      headerIndex = i
      columns = candidate
      break
    }
  }

  if (!columns) {
    return empty(
      'Vi hittar ingen kolumn för meddelande och belopp i filen. ' +
        'Kontrollera att det är transaktionsrapporten från Swish du laddat upp, ' +
        'och att den är sparad som CSV.',
    )
  }

  const byReference = new Map(orders.map((order) => [order.reference, order]))
  const rows: ReportRow[] = []

  for (let i = headerIndex + 1; i < table.length; i++) {
    const cells = table[i]
    const get = (index: number) => (index > -1 ? (cells[index] ?? '') : '')

    const message = get(columns.message)
    const amountSek = parseAmount(get(columns.amount))
    const rowStatus = get(columns.status).toLowerCase()

    const row: ReportRow = {
      line: i + 1,
      date: get(columns.date),
      message,
      sender: get(columns.sender),
      amountSek,
      reference: parseOrderReference(message),
      status: 'noReference',
    }

    // Återbetalningar och avvisade transaktioner ska inte bocka av något.
    if (amountSek === null || amountSek <= 0 || /återbet|refund|avvis|makul/.test(rowStatus)) {
      row.status = 'notAPayment'
      rows.push(row)
      continue
    }

    if (!row.reference) {
      row.status = 'noReference'
      rows.push(row)
      continue
    }

    const order = byReference.get(row.reference)
    if (!order) {
      row.status = 'unknownReference'
      rows.push(row)
      continue
    }

    row.expectedSek = order.totalSek
    row.buyerName = order.buyerName

    if (order.status === 'paid') row.status = 'alreadyPaid'
    else if (order.status === 'cancelled') row.status = 'cancelled'
    else if (Math.round(amountSek) !== Math.round(order.totalSek)) row.status = 'amountMismatch'
    else row.status = 'matched'

    rows.push(row)
  }

  const counts = emptyCounts()
  for (const row of rows) counts[row.status] += 1

  /*
   * Bara de som matchar på både referens och belopp föreslås. En betalning på
   * fel belopp är inte skräp att städa bort – den är något någon behöver
   * titta på, och att bocka av den automatiskt hade dolt problemet.
   *
   * Dubbletter tas bort: samma referens kan förekomma två gånger om någon
   * swishat två gånger, och beställningen ska ändå bara betalas en gång.
   */
  const readyToSettle = [
    ...new Set(rows.filter((row) => row.status === 'matched').map((row) => row.reference as string)),
  ]

  return { rows, readyToSettle, counts, problem: null }
}

function emptyCounts(): Record<MatchStatus, number> {
  return {
    matched: 0,
    amountMismatch: 0,
    alreadyPaid: 0,
    cancelled: 0,
    unknownReference: 0,
    noReference: 0,
    notAPayment: 0,
  }
}

/* ------------------------------------------------------------------ *
 * Exempelrapport för demo
 * ------------------------------------------------------------------ */

/**
 * Bygger en Swish-rapport av kända beställningar.
 *
 * Avstämningen är den funktion som är svårast att visa upp, av den banala
 * anledningen att den kräver en fil man rimligen inte har liggande. Utan en
 * sådan går det att berätta om funktionen men inte att demonstrera den, och
 * det är skillnad.
 *
 * Filen innehåller med flit inte bara rader som stämmer. Vyns hela poäng är
 * att den skiljer på fem utfall, och en rapport där allt matchar visar ett av
 * dem. Den som får filen ska se alla fem:
 *
 *   matched           – stämmer på både referens och belopp
 *   amountMismatch    – rätt referens, fel belopp
 *   alreadyPaid       – en betalning för något redan avbockat
 *   unknownReference  – en referens som inte finns hos oss
 *   noReference       – någon annans betalning till föreningen
 *
 * Radordningen är blandad på samma sätt som i en riktig fil: avstämningen får
 * inte förutsätta att häftesbetalningarna ligger i följd.
 */
export function sampleSwishReport(
  orders: KnownOrder[],
  options: { date?: string } = {},
): string {
  const date = options.date ?? '2026-05-18'
  const awaiting = orders.filter((order) => order.status === 'awaitingPayment')
  const paid = orders.find((order) => order.status === 'paid')

  let minute = 0
  const row = (name: string, amount: number, message: string) => {
    minute += 23
    const time = `${String(9 + Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
    return [`${date} ${time}`, 'Swish', name, formatAmount(amount), 'SEK', message].join(';')
  }

  const rows: string[] = []

  awaiting.forEach((order, index) => {
    // Den näst sista betalar tjugo kronor för lite. Med bara en obetald
    // beställning får den stämma – en demo som inte kan bocka av någonting
    // alls är sämre än en utan felrad.
    const wrongAmount = awaiting.length >= 2 && index === awaiting.length - 2
    rows.push(
      row(
        order.buyerName,
        wrongAmount ? order.totalSek - 20 : order.totalSek,
        orderPaymentMessage(order.reference),
      ),
    )
  })

  // Någon annans betalning till föreningen. Verkliga rapporter är fulla av
  // dem, och att de sorteras bort tyst är en del av poängen.
  rows.splice(Math.min(1, rows.length), 0, row('Medlemsavgift Persson', 900, 'Traningsavgift host'))

  // En referens som inte finns hos oss, som när en beställning avbrutits
  // efter att föräldern redan fått betaluppgifterna.
  rows.push(row('Mikael Ek', 220, `${ORDER_PREFIX}ZZZZZZ`))

  // En betalning för något som redan är avbockat. Dubbelswishar händer.
  if (paid) rows.push(row(paid.buyerName, paid.totalSek, orderPaymentMessage(paid.reference)))

  const header = ['Datum', 'Typ', 'Avsandare', 'Belopp', 'Valuta', 'Meddelande'].join(';')
  return [header, ...rows].join('\r\n') + '\r\n'
}

/** Belopp i det format Swish skriver dem: mellanslag som tusental, komma som decimal. */
function formatAmount(amount: number): string {
  const whole = Math.trunc(amount)
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${grouped},00`
}
